import {AnalyticsEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';

// Keep in sync with benchling/analytics/lib/config_migration_analytics_event.py
export enum ConfigMigrationAnalyticsAction {
  // Export Stage
  INITIATED_EXPORT_ORGANIZATION_CONFIG = 'initiated_export_organization_config',
  COMPLETED_EXPORT_ORGANIZATION_CONFIG = 'completed_export_organization_config',

  // Import Stage
  INITIATED_IMPORT_ORGANIZATION_CONFIG_FILE = 'initiated_import_organization_config_file',
  COMPLETED_IMPORT_ORGANIZATION_CONFIG_FILE = 'completed_import_organization_config_file',

  INITIATED_EXPORT_CONFIG_MIGRATION_COMPARISON_FILE = 'initiated_export_config_migration_comparison_file',
  COMPLETED_EXPORT_CONFIG_MIGRATION_COMPARISON_FILE = 'completed_export_config_migration_comparison_file',

  INITIATED_VALIDATE_CONFIG = 'initiated_validate_config',
  COMPLETED_VALIDATE_CONFIG = 'completed_validate_config',

  // Validate Plan (new tabs UI - validates all objects at once)
  INITIATED_VALIDATE_CONFIG_TABS = 'initiated_validate_config_tabs',
  COMPLETED_VALIDATE_CONFIG_TABS = 'completed_validate_config_tabs',

  INITIATED_CONFIG_MIGRATION_IMPORT_PANE_ACTION = 'initiated_config_migration_import_pane_action',

  OPENED_UNCHANGED_ITEMS = 'opened_unchanged_items',

  INITIATED_IMPORT_CONFIGURATION = 'initiated_import_configuration',
  COMPLETED_IMPORT_CONFIGURATION = 'completed_import_configuration',

  // Import Configuration (new tabs UI - imports all objects at once)
  INITIATED_IMPORT_CONFIGURATION_TABS = 'initiated_import_configuration_tabs',
  COMPLETED_IMPORT_CONFIGURATION_TABS = 'completed_import_configuration_tabs',

  EXPORTED_MIGRATION_SUMMARY = 'exported_migration_summary',

  // Migration Management History Page
  EXPORTED_HISTORY_CHANGELOG_FILE = 'exported_history_changelog_file',

  // Migration Experience Toggle
  CLICKED_OLD_MIGRATION_EXPERIENCE = 'clicked_old_migration_experience',
  CLICKED_NEW_MIGRATION_EXPERIENCE = 'clicked_new_migration_experience',
}

function createConfigMigrationAnalyticsEvent(
  action: ConfigMigrationAnalyticsAction,
  actionCategory: ACTION_CATEGORY = ACTION_CATEGORY.READ
): AnalyticsEvent {
  return {
    module: ANALYTICS_MODULE.CONFIG_MIGRATION,
    action,
    actionCategory,
  };
}

// Keep in sync with benchling/analytics/lib/config_migration_analytics_event.py
export const CONFIG_MIGRATION_ANALYTICS_ACTIONS: {[key in ConfigMigrationAnalyticsAction]: AnalyticsEvent} = {
  // Export Stage
  [ConfigMigrationAnalyticsAction.INITIATED_EXPORT_ORGANIZATION_CONFIG]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.INITIATED_EXPORT_ORGANIZATION_CONFIG
  ),
  [ConfigMigrationAnalyticsAction.COMPLETED_EXPORT_ORGANIZATION_CONFIG]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.COMPLETED_EXPORT_ORGANIZATION_CONFIG
  ),

  // Import Stage
  [ConfigMigrationAnalyticsAction.INITIATED_IMPORT_ORGANIZATION_CONFIG_FILE]:
    createConfigMigrationAnalyticsEvent(
      ConfigMigrationAnalyticsAction.INITIATED_IMPORT_ORGANIZATION_CONFIG_FILE
    ),
  [ConfigMigrationAnalyticsAction.COMPLETED_IMPORT_ORGANIZATION_CONFIG_FILE]:
    createConfigMigrationAnalyticsEvent(
      ConfigMigrationAnalyticsAction.COMPLETED_IMPORT_ORGANIZATION_CONFIG_FILE
    ),

  [ConfigMigrationAnalyticsAction.INITIATED_EXPORT_CONFIG_MIGRATION_COMPARISON_FILE]:
    createConfigMigrationAnalyticsEvent(
      ConfigMigrationAnalyticsAction.INITIATED_EXPORT_CONFIG_MIGRATION_COMPARISON_FILE
    ),
  [ConfigMigrationAnalyticsAction.COMPLETED_EXPORT_CONFIG_MIGRATION_COMPARISON_FILE]:
    createConfigMigrationAnalyticsEvent(
      ConfigMigrationAnalyticsAction.COMPLETED_EXPORT_CONFIG_MIGRATION_COMPARISON_FILE
    ),

  [ConfigMigrationAnalyticsAction.INITIATED_VALIDATE_CONFIG]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.INITIATED_VALIDATE_CONFIG
  ),
  [ConfigMigrationAnalyticsAction.COMPLETED_VALIDATE_CONFIG]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.COMPLETED_VALIDATE_CONFIG
  ),

  // Validate Plan (new tabs UI)
  [ConfigMigrationAnalyticsAction.INITIATED_VALIDATE_CONFIG_TABS]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.INITIATED_VALIDATE_CONFIG_TABS
  ),
  [ConfigMigrationAnalyticsAction.COMPLETED_VALIDATE_CONFIG_TABS]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.COMPLETED_VALIDATE_CONFIG_TABS
  ),

  [ConfigMigrationAnalyticsAction.INITIATED_CONFIG_MIGRATION_IMPORT_PANE_ACTION]:
    createConfigMigrationAnalyticsEvent(
      ConfigMigrationAnalyticsAction.INITIATED_CONFIG_MIGRATION_IMPORT_PANE_ACTION
    ),

  [ConfigMigrationAnalyticsAction.OPENED_UNCHANGED_ITEMS]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.OPENED_UNCHANGED_ITEMS
  ),

  [ConfigMigrationAnalyticsAction.INITIATED_IMPORT_CONFIGURATION]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.INITIATED_IMPORT_CONFIGURATION,
    ACTION_CATEGORY.WRITE
  ),
  [ConfigMigrationAnalyticsAction.COMPLETED_IMPORT_CONFIGURATION]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.COMPLETED_IMPORT_CONFIGURATION,
    ACTION_CATEGORY.WRITE
  ),

  // Import Configuration (new tabs UI)
  [ConfigMigrationAnalyticsAction.INITIATED_IMPORT_CONFIGURATION_TABS]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.INITIATED_IMPORT_CONFIGURATION_TABS,
    ACTION_CATEGORY.WRITE
  ),
  [ConfigMigrationAnalyticsAction.COMPLETED_IMPORT_CONFIGURATION_TABS]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.COMPLETED_IMPORT_CONFIGURATION_TABS,
    ACTION_CATEGORY.WRITE
  ),

  [ConfigMigrationAnalyticsAction.EXPORTED_MIGRATION_SUMMARY]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.EXPORTED_MIGRATION_SUMMARY
  ),

  // Migration Management History Page
  [ConfigMigrationAnalyticsAction.EXPORTED_HISTORY_CHANGELOG_FILE]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.EXPORTED_HISTORY_CHANGELOG_FILE
  ),

  // Migration Experience Toggle
  [ConfigMigrationAnalyticsAction.CLICKED_OLD_MIGRATION_EXPERIENCE]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.CLICKED_OLD_MIGRATION_EXPERIENCE
  ),
  [ConfigMigrationAnalyticsAction.CLICKED_NEW_MIGRATION_EXPERIENCE]: createConfigMigrationAnalyticsEvent(
    ConfigMigrationAnalyticsAction.CLICKED_NEW_MIGRATION_EXPERIENCE
  ),
};

// Keep in sync with benchling/analytics/lib/config_migration_analytics_event.py
export enum TenantComparisonAnalyticsAction {
  INITIATED_CONFIG_EXPORT = 'initiated_config_export',
  COMPLETED_CONFIG_EXPORT = 'completed_config_export',
  INITIATED_CONFIG_DIFF = 'initiated_config_diff',
  COMPLETED_CONFIG_DIFF = 'completed_config_diff',
}

function createTenantComparisonReportAnalyticsEvent(
  action: TenantComparisonAnalyticsAction,
  actionCategory: ACTION_CATEGORY = ACTION_CATEGORY.READ
): AnalyticsEvent {
  return {
    module: ANALYTICS_MODULE.TENANT_COMPARISON,
    action,
    actionCategory,
  };
}
export const TENANT_COMPARISON_ANALYTICS_ACTIONS: {[key in TenantComparisonAnalyticsAction]: AnalyticsEvent} =
  {
    // Export Stage
    [TenantComparisonAnalyticsAction.INITIATED_CONFIG_EXPORT]: createTenantComparisonReportAnalyticsEvent(
      TenantComparisonAnalyticsAction.INITIATED_CONFIG_EXPORT
    ),
    [TenantComparisonAnalyticsAction.COMPLETED_CONFIG_EXPORT]: createTenantComparisonReportAnalyticsEvent(
      TenantComparisonAnalyticsAction.COMPLETED_CONFIG_EXPORT
    ),
    [TenantComparisonAnalyticsAction.INITIATED_CONFIG_DIFF]: createTenantComparisonReportAnalyticsEvent(
      TenantComparisonAnalyticsAction.INITIATED_CONFIG_DIFF
    ),
    [TenantComparisonAnalyticsAction.COMPLETED_CONFIG_DIFF]: createTenantComparisonReportAnalyticsEvent(
      TenantComparisonAnalyticsAction.COMPLETED_CONFIG_DIFF
    ),
  };
